import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useHistory, useLocation } from "react-router-dom";
import logo from "../../images/logo.png";
import EyeIcon from "../../icons/eye.svg";
import ChevronIcon from "../../icons/chevron-down.svg";
import EyeOffIcon from "../../icons/eye-off.svg";
import closeIcon from "../../icons/xW.svg";
import ReCAPTCHA from "react-google-recaptcha";
import SnackbarNotification from "../SnackbarNotification";
import { confAxios } from "../../config/api";

function useBreakpoint(query = "(min-width: 768px)") {
  const [matches, setMatches] = useState(
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    mql.addEventListener("change", onChange);
    setMatches(mql.matches);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

function Banner({ type = "success", onClose, children }) {
  const ok = type === "success";
  return (
    <div
      className={`relative font-semibold rounded-[12px] pl-4 pr-[18px] py-3 ${ok ? "bg-[#0AD69FC7] border-2 border-[#13C190] text-white" : "bg-[#EF466FA8] border-2 border-[#EF466F] text-white"
        }`}
    >
      <img
        src={closeIcon}
        alt="close"
        className="absolute top-2 right-2 w-4 h-4 cursor-pointer"
        onClick={onClose}
      />
      {children}
    </div>
  );
}

function useSignupLogic() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [formData, setFormData] = useState({
    companyName: "",
    contactFirstName: "",
    contactLastName: "",
    contactEmail: "",
    companyPhone: "",
    preferredLanguage: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    const browserLang = (navigator.language || "en").split("-")[0];
    const lang = ["en", "fr"].includes(browserLang) ? browserLang : "en";
    i18n.changeLanguage(lang);
  }, [i18n]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [banner, setBanner] = useState(location.state?.banner || null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    manual: false,
  });

  const showSnackbar = (message, severity = "success", manual = false) =>
    setSnackbar({ open: true, message, severity, manual });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = [
      "companyName",
      "companyPhone",
      "contactFirstName",
      "contactLastName",
      "contactEmail",
      "preferredLanguage",
      "password",
      "confirmPassword",
    ];

    const missing = requiredFields.filter(
      (k) => !String(formData[k] ?? "").trim()
    );
    if (missing.length > 0 || !recaptchaToken) {
      showSnackbar(
        t("signup_fields.required_fields"),
        "error",
        true
      );
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      showSnackbar(t("signup_fields.password_mismatch"), "error", true);
      return;
    }

    const payload = {
      companyName: formData.companyName,
      contactFirstName: formData.contactFirstName,
      contactLastName: formData.contactLastName,
      contactEmail: formData.contactEmail,
      password: formData.password,
      phoneNumber: formData.companyPhone,
      preferredLanguage: formData.preferredLanguage,
      token: recaptchaToken,
    };

    try {
      setIsSubmitting(true);

      const res = await fetch(`${confAxios.baseURL}/users/signup`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const clone = res.clone();
        const ct = clone.headers.get("content-type") || "";
        let body;
        try {
          body = ct.includes("application/json") ? await clone.json() : await clone.text();
        } catch {
          body = "<no body / parse failed>";
        }
        console.log("Signup error body:", body);
        showSnackbar(body.statusLabel || "Signup failed", "error", true);
        return;
      }

      setShowSuccessBanner(true);
      setRecaptchaToken("");
      
      // Clear form values after successful signup
      setFormData({
        companyName: "",
        contactFirstName: "",
        contactLastName: "",
        contactEmail: "",
        companyPhone: "",
        preferredLanguage: "",
        password: "",
        confirmPassword: "",
      });
    } catch (err) {
      console.error("Signup request failed:", err);
      showSnackbar("Network error. Please try again.", "error", true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    t,
    history,
    formData,
    setFormData,
    handleChange,
    handleSubmit,
    showPassword,
    setShowPassword,
    showConfirm,
    setShowConfirm,
    isSubmitting,
    recaptchaToken,
    setRecaptchaToken,
    showSuccessBanner,
    setShowSuccessBanner,
    banner,
    setBanner,
    snackbar,
    setSnackbar,
    showSnackbar,
  };
}

function useFields(t) {
  return useMemo(
    () => [
      { label: t("signup_fields.companyName"), name: "companyName", autoComplete: "organization" },
      { label: t("signup_fields.companyPhone"), name: "companyPhone", type: "tel", autoComplete: "tel" },
      { label: t("signup_fields.contactFirstName"), name: "contactFirstName", autoComplete: "given-name" },
      { label: t("signup_fields.contactLastName"), name: "contactLastName", autoComplete: "family-name" },
      { label: t("signup_fields.contactEmail"), name: "contactEmail", type: "email", autoComplete: "email" },
      { label: t("signup_fields.preferredLanguage"), name: "preferredLanguage", type: "select" },
      { label: t("signup_fields.password"), name: "password", type: "password", autoComplete: "new-password" },
      { label: t("signup_fields.confirmPassword"), name: "confirmPassword", type: "password", autoComplete: "new-password" },
    ],
    [t]
  );
}

const baseInput =
  [
    "w-full px-4 py-3 font-medium placeholder-[#AFB0B2]",
    "border border-gray-300 bg-white text-gray-600 rounded-[12px]",
    "focus:outline-none focus:ring-2 focus:ring-indigo-500",
    "md:border-[#E3E3E3] md:text-gray-800 md:rounded-lg md:shadow-sm",
  ].join(" ");

function FieldsGrid({
  fields,
  formData,
  handleChange,
  showPassword,
  setShowPassword,
  showConfirm,
  setShowConfirm,
}) {
  const isMdUp = useBreakpoint("(min-width: 768px)");

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-5 mb-0 md:mb-6">
      {fields.map(({ label, name, type = "text", autoComplete }) => {
        const Label = (
          <label
            htmlFor={name}
            className="hidden md:!block text-[#38485C] font-semibold mb-1 text-left"
          >
            {label}
            <span className="text-red-500 ml-1">*</span>
          </label>
        );

        const placeholder = isMdUp ? label : `${label} *`;

        if (type === "select") {
          return (
            <div key={name} className="text-left">
              {Label}
              <div className="relative">
                <select
                  id={name}
                  name="preferredLanguage"
                  value={formData.preferredLanguage}
                  onChange={handleChange}
                  aria-required="true"
                  className={`${baseInput} appearance-none pr-10 ${formData.preferredLanguage ? "text-gray-800" : "text-[#AFB0B2]"
                    }`}
                >
                  <option value="" disabled hidden>
                    {placeholder}
                  </option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                </select>
                <img
                  src={ChevronIcon}
                  alt=""
                  className="pointer-events-none w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2"
                />
              </div>
            </div>
          );
        }

        if (type === "password" && name === "password") {
          return (
            <div key={name} className="text-left">
              {Label}
              <div className="relative">
                <input
                  id={name}
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={placeholder}
                  autoComplete={autoComplete}
                  aria-required="true"
                  className={baseInput}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-3"
                  aria-label="Toggle password visibility"
                >
                  <img src={showPassword ? EyeOffIcon : EyeIcon} alt="" className="w-5 h-5" />
                </button>
              </div>
            </div>
          );
        }

        if (type === "password" && name === "confirmPassword") {
          return (
            <div key={name} className="text-left">
              {Label}
              <div className="relative">
                <input
                  id={name}
                  type={showConfirm ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder={placeholder}
                  autoComplete={autoComplete}
                  aria-required="true"
                  className={baseInput}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((s) => !s)}
                  className="absolute right-3 top-3"
                  aria-label="Toggle confirm password visibility"
                >
                  <img src={showConfirm ? EyeOffIcon : EyeIcon} alt="" className="w-5 h-5" />
                </button>
              </div>
            </div>
          );
        }

        return (
          <div key={name} className="text-left">
            {Label}
            <input
              id={name}
              type={type}
              name={name}
              value={formData[name]}
              onChange={handleChange}
              placeholder={placeholder}
              autoComplete={autoComplete}
              aria-required="true"
              className={baseInput}
            />
          </div>
        );
      })}
    </div>
  );
}


function SignupDesktop(props) {
  const {
    t,
    history,
    fields,
    formData,
    handleChange,
    handleSubmit,
    showPassword,
    setShowPassword,
    showConfirm,
    setShowConfirm,
    isSubmitting,
    setRecaptchaToken,
    showSuccessBanner,
    setShowSuccessBanner,
    banner,
    setBanner,
    snackbar,
    setSnackbar,
  } = props;

  return (
    <>
      {showSuccessBanner && (
        <div className="fixed top-8 right-4 max-w-[550px] z-50">
          <Banner type="success" onClose={() => setShowSuccessBanner(false)}>
            {t("placeholder.emailVerification") || "Verification email sent. Please check your inbox."}
          </Banner>
        </div>
      )}
      {banner && (
        <div className="fixed top-8 right-4 max-w-[550px] z-50">
          <Banner type={banner.type || "success"} onClose={() => setBanner(null)}>
            {banner.message}
          </Banner>
        </div>
      )}
      <div className="absolute bottom-0 left-0 h-full w-1/2 bg-cover bg-center"
        style={{ backgroundImage: `url(${require('../../images/background.png')})`, backgroundSize: "100%", backgroundPosition: "center" }}>
      </div>

      <div className="relative w-full max-w-[480px] md:max-w-none md:w-[40%] z-10 text-center px-4 md:px-0">
        <form onSubmit={handleSubmit}>
          <img src={logo} alt="GrayFox Logo" className="mx-auto w-[200px] md:w-[250px] mb-6 mt-6 md:mt-4" />
          <h2 className="text-[22px] md:text-[28px] font-semibold text-[#38485C] mb-6">
            {t("login.signup") || "Sign Up"}
          </h2>

          <FieldsGrid
            fields={fields}
            formData={formData}
            handleChange={handleChange}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            showConfirm={showConfirm}
            setShowConfirm={setShowConfirm}
          />

          <div className="flex justify-center my-4">
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_CAPTCHA}
              size="normal"
              onChange={(token) => setRecaptchaToken(token || "")}
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 bg-primary text-white text-[18px] rounded-[12px] hover:bg-indigo-500 transition font-semibold shadow-md ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""
              }`}
          >
            {t("login.signup") || "Sign Up"}
          </button>

          <div className="mt-4 text-center">
            <p className="text-[#38485C] text-[16px]">
              {t("login.have_account") || "Already have an account?"}{" "}
              <button
                type="button"
                onClick={() => history.push("/login")}
                className="text-primary hover:underline font-semibold"
              >
                {t("login.title")}
              </button>
            </p>
          </div>
        </form>
      </div>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </>
  );
}

/** Mobile layout */
function SignupMobile(props) {
  const {
    t,
    fields,
    formData,
    handleChange,
    handleSubmit,
    showPassword,
    setShowPassword,
    showConfirm,
    setShowConfirm,
    isSubmitting,
    setRecaptchaToken,
    showSuccessBanner,
    setShowSuccessBanner,
    banner,
    setBanner,
    snackbar,
    setSnackbar,
  } = props;

  const captchaRef = useRef(null);
  const [showCaptcha, setShowCaptcha] = useState(true);

  useEffect(() => {
    if (banner || showSuccessBanner) setShowCaptcha(false);
  }, [banner, showSuccessBanner]);

  const onSubmitMobile = (e) => {
    if (!showCaptcha) {
      e.preventDefault();
      setShowCaptcha(true);
      setTimeout(() => {
        captchaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    handleSubmit(e);
  };

  const activeBanner = showSuccessBanner
    ? { type: "success", message: t("placeholder.emailVerification") || "Verification email sent. Please check your inbox." }
    : banner;

  const logoTopOffsetStyle = { marginTop: "clamp(20px, 6vh, 56px)" };


  return (
    <>
      <div className="relative w-full max-w-[480px] z-10 text-center px-6">
        <div
          className="absolute inset-x-0 bottom-0 w-full h-1/3 bg-cover bg-no-repeat bg-top pointer-events-none"
          style={{
            backgroundImage: `url(${require("../../images/Image.png")})`,
            backgroundSize: "110% auto",
          }}
        />

        <form onSubmit={onSubmitMobile} className="min-h-[100vh] flex flex-col">
          <div style={logoTopOffsetStyle} />

          <div className="flex justify-center">
            <img src={logo} alt="GrayFox Logo" className="w-[200px] md:w-[250px]" />
          </div>

          <h2 className="mt-4 text-[22px] font-semibold text-[#38485C]">
            {t("login.signup") || "Sign Up"}
          </h2>

          <div className="mt-6">
            <FieldsGrid
              fields={fields}
              formData={formData}
              handleChange={handleChange}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              showConfirm={showConfirm}
              setShowConfirm={setShowConfirm}
            />
          </div>

          <div ref={captchaRef} className="my-3">
            {showCaptcha ? (
              <div className="flex justify-center">
                <div className="transform origin-top scale-90">
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_CAPTCHA}
                    onChange={(token) => setRecaptchaToken(token || "")}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center text-xs text-gray-500">
                <button type="button" onClick={() => setShowCaptcha(true)} className="underline font-medium">
                  {t("cta.show_captcha") || "Show CAPTCHA to sign up"}
                </button>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 bg-primary text-white text-[18px] rounded-[12px] hover:bg-indigo-500 transition font-semibold shadow-md ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""
              }`}
          >
            {t("login.signup") || "Sign Up"}
          </button>

          <div className="mt-3 mb-3">
            {activeBanner && (
              <Banner
                type={activeBanner.type || "success"}
                onClose={() => {
                  if (showSuccessBanner) setShowSuccessBanner(false);
                  else setBanner(null);
                }}
              >
                {activeBanner.message}
              </Banner>
            )}
          </div>
        </form>
      </div>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </>
  );
}

export default function Signup() {
  const isMdUp = useBreakpoint("(min-width: 768px)");
  const logic = useSignupLogic();
  const fields = useFields(logic.t);

  return (
    <div className="relative flex items-center justify-center min-h-screen bg-gray-100 overflow-hidden">
      <Helmet>
        <title>GrayFox - Sign Up</title>
      </Helmet>

      <div className="absolute top-0 left-0 w-full h-40 bg-blur opacity-20 blur-3xl" />

      <div
        className="hidden md:block absolute bottom-0 right-0 h-full w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: `url(${require("../../images/background.png")})`,
          backgroundSize: "100%",
          backgroundPosition: "center",
        }}
      />

      {isMdUp ? (
        <SignupDesktop {...logic} fields={fields} />
      ) : (
        <SignupMobile {...logic} fields={fields} />
      )}
    </div>
  );
}
